import React, { useMemo } from "react";
import ReportResultsTable from "../shared/ReportResultsTable";

function CrossSellReportBuilder({
  coverageTypes,
  coverageTypesLoading,
  coverageTypesError,
  carriers,
  carriersLoading,
  selectedOwnedCoverageTypes,
  setSelectedOwnedCoverageTypes,
  selectedMissingCoverageTypes,
  setSelectedMissingCoverageTypes,
  selectedCarrierId,
  setSelectedCarrierId,
  stateFilter,
  setStateFilter,
  zipCodeFilter,
  setZipCodeFilter,
  onClearFilters,
  reportResults,
  builtConfig,
  formatFieldName
}) {
  const statesList = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];

  const toggleCoverageType = (setSelectedValues, id) => {
    const normalizedId = String(id);

    setSelectedValues((prev) => {
      if (prev.includes(normalizedId)) {
        return prev.filter((item) => item !== normalizedId);
      }
      return [...prev, normalizedId];
    });
  };

  const selectedColumns = reportResults?.selectedColumns || builtConfig.selectedColumns || [];
  const rows = reportResults?.rows || [];
  const subtitle = reportResults
    ? `${reportResults.rowCount} row${reportResults.rowCount === 1 ? "" : "s"}`
    : "Run the report to view matching clients";

  const clearDisabled = useMemo(
    () => !selectedCarrierId && !stateFilter && !zipCodeFilter.trim(),
    [selectedCarrierId, stateFilter, zipCodeFilter]
  );

  const renderCheckboxSelector = (
    title,
    description,
    selectedValues,
    setSelectedValues
  ) => {
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: "8px" }}>
          <div style={{ fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
            {title}
          </div>
          <div style={{ fontSize: "13px", color: "#64748b", marginTop: "2px" }}>
            {description}
          </div>
        </div>

        <div
          style={{
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            background: "#ffffff",
            minHeight: "290px",
            padding: "8px 10px",
            overflowY: "auto"
          }}
        >
          {coverageTypesLoading ? (
            <div style={{ color: "#64748b", fontSize: "14px" }}>
              Loading coverage types...
            </div>
          ) : coverageTypes.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: "14px" }}>
              No coverage types available.
              {coverageTypesError ? ` (${coverageTypesError})` : ""}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {coverageTypes.map((coverageType) => {
                const checked = selectedValues.includes(String(coverageType.id));

                return (
                  <label
                    key={coverageType.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "5px 4px",
                      borderBottom: "1px solid #f1f5f9",
                      cursor: "pointer",
                      fontSize: "13px",
                      color: "#1e293b",
                      lineHeight: 1.2
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        toggleCoverageType(setSelectedValues, coverageType.id)
                      }
                      style={{ width: "14px", height: "14px", margin: 0 }}
                    />
                    <span>{coverageType.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const resultsToolbar = (
    <div className="sales-toolbar-card" style={{ marginBottom: "14px" }}>
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <select
          value={selectedCarrierId}
          onChange={(e) => setSelectedCarrierId(e.target.value)}
          className="sales-search-input"
          disabled={carriersLoading}
          style={{ maxWidth: "220px" }}
        >
          <option value="">All Carriers</option>
          {carriers.map((carrier) => (
            <option key={carrier.id} value={carrier.id}>
              {carrier.name || carrier.carrier_name}
            </option>
          ))}
        </select>

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="sales-search-input"
          style={{ maxWidth: "180px" }}
        >
          <option value="">All States</option>
          {statesList.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="sales-search-input"
          placeholder="Exact zip code"
          value={zipCodeFilter}
          onChange={(e) => setZipCodeFilter(e.target.value)}
          style={{ maxWidth: "180px" }}
        />

        <button
          type="button"
          className="sales-secondary-btn"
          onClick={onClearFilters}
          disabled={clearDisabled}
        >
          Clear Filters
        </button>
      </div>
    </div>
  );

  return (
    <>
      {coverageTypesError && (
        <div style={{ marginBottom: "10px" }}>
          <div className="sales-section-card">
            <div className="sales-section-head">
              <h2>Coverage Types Load Error</h2>
              <p>{coverageTypesError}</p>
            </div>
          </div>
        </div>
      )}

      <div className="sales-section-card">
        <div className="sales-section-head">
          <h2>Coverage Criteria</h2>
          <p>
            Select the active coverage clients already carry and the active coverage they do not currently hold.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            alignItems: "start"
          }}
        >
          {renderCheckboxSelector(
            "Required Existing Coverage",
            "Active coverage the client must already have.",
            selectedOwnedCoverageTypes,
            setSelectedOwnedCoverageTypes
          )}

          {renderCheckboxSelector(
            "Target Coverage Gap",
            "Active coverage the client must not already have.",
            selectedMissingCoverageTypes,
            setSelectedMissingCoverageTypes
          )}
        </div>
      </div>

      <div style={{ marginTop: "10px" }}>
        <ReportResultsTable
          title="Report Results"
          subtitle={subtitle}
          selectedColumns={selectedColumns}
          rows={rows}
          emptyMessage={
            reportResults ? "No matching clients found." : "Run the report to view results."
          }
          toolbar={resultsToolbar}
          formatFieldName={formatFieldName}
        />
      </div>
    </>
  );
}

export default CrossSellReportBuilder;